import React, { useCallback, useEffect, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { addDays, format } from "date-fns";
import CustomDatePicker from "./datepicker";
import { formatDate, transformToDate } from "./utils";
import {
  ChartTitle,
  ReportWrapper,
  Subtitle,
  DatepickerRow,
  ChartWrapper,
  colors,
} from "./styles";
import { useQueryReport } from "../hooks/useQueryReport";
import { Card, CardBody, CardHeader, CardTitle } from "reactstrap";

const SourceReport = (props) => {
  const INITIAL_STATE = {
    labels: [],
    datasets: [],
  };
  const [reportData, setReportData] = useState(INITIAL_STATE);
  const [startDate, setStartDate] = useState(addDays(new Date(), -10));
  const [endDate, setEndDate] = useState(new Date());
  const [totalSources, setTotalSources] = useState(0);

  const { fetchData } = useQueryReport();

  const transformAPIData = (data) => {
    let transformedData = [];
    let datesArray = [];
    data.forEach((row) => {
      transformedData.push({
        date: formatDate(row.dimensionValues[1].value),
        source: row.dimensionValues[0],
        visits: row.metricValues[0].value[0],
      });
      datesArray.push(transformToDate(row.dimensionValues[1].value));

    });
    return [transformedData, datesArray];
  };

  const groupDataBySource = (data) => {

    return data.reduce((r, a) => {

      r[a.source.value] = r[a.source.value] || [];
      r[a.source.value].push(a);
      return r;
    }, Object.create(null));
  };

  const sortSourcesByTotalVisits = (data) => {
    let sumedVisits = [];
    for (let [key, value] of Object.entries(data)) {
      const sumOfVisits = value.reduce((a, b) => {
        return a + parseInt(b.visits);
      }, 0);
      sumedVisits.push({
        source: key,
        visits: sumOfVisits,
      });
    }
    return sumedVisits.sort((a, b) => b.visits - a.visits);
  };

  const createDataForChart = (datesArray, sumedVisits, groupedBySource) => {
    datesArray.sort((a, b) => {
      return new Date(a) - new Date(b);
    });
    const datesFormatted = datesArray.map((date) =>
      format(new Date(date), "MMM. d, yyyy")
    );
    const uniqueDates = [...new Set(datesFormatted)];
    let datasetsArray = [];
    let i = 0;
    sumedVisits.forEach((item, id) => {
      console.log(sumedVisits);
      if (id < 5) {
        const label = item.source;
        const backgroundColor = colors[i + 6];
        i++;
        let data = [];
        uniqueDates.forEach((date) => {

          const row = groupedBySource[item.source].find(
            (item) => item.date === date
          );
          if (row) {
            data.push(parseInt(row.visits));
          } else {
            data.push(0);
          }
        });
        datasetsArray.push({
          label,
          backgroundColor,
          data,
        });
      }
    });
    return { labels: uniqueDates, data: datasetsArray };
  };

  const displayResults = useCallback((response) => {
    const queryResult = response.reports[0].rows;

    const data = transformAPIData(queryResult);
    let transformedData = data[0];
    let datesArray = data[1];


    const groupedBySource = groupDataBySource(transformedData);
    setTotalSources(Object.keys(groupedBySource).length);



    const sumedVisits = sortSourcesByTotalVisits(groupedBySource);

    const dataForChart = createDataForChart(
      datesArray,
      sumedVisits,
      groupedBySource
    );

    setReportData((prev) => ({
      ...prev,
      labels: dataForChart.labels,
      datasets: dataForChart.data,
    }));

  }, []);

  const options = {
    tooltips: {
      displayColors: true,
      callbacks: {
        mode: "x",
      },
    },
    scales: {
      xAxes: {
        stacked: true,
        gridLines: {
          drawBorder: false,
          color: "rgba(225,78,202,0.1)",
          zeroLineColor: "transparent"
        },
        ticks: {
          padding: 20,
          fontColor: "#9e9e9e"
        }

      },
      yAxes: [
        {
          gridLines: {
            drawBorder: false,
            color: "rgba(225,78,202,0.1)",
            zeroLineColor: "transparent"
          },
          ticks: {
            padding: 20,
            fontColor: "#9e9e9e"
          }
        }
      ],
    },
    maintainAspectRatio: false,
    legend: { position: "bottom" },
    plugins: {
      datalabels: {
        font: {
          size: 0,
        },
      },
    },
  };

  const data = {
    labels: reportData.labels,
    datasets: reportData.datasets,
  };
  let chartExample1 = {
    data1: (canvas) => {
      let ctx = canvas.getContext("2d");

      let gradientStroke = ctx.createLinearGradient(0, 230, 0, 50);

      gradientStroke.addColorStop(1, "rgba(29,140,248,0.2)");
      gradientStroke.addColorStop(0.4, "rgba(29,140,248,0.0)");
      gradientStroke.addColorStop(0, "rgba(29,140,248,0)"); //blue colors

      return {
        labels: reportData.labels,
        datasets: [
          {
            label: reportData.labels,
            fill: true,
            backgroundColor: gradientStroke,
            borderColor: "#1f8ef1",
            borderWidth: 2,
            borderDash: [],
            borderDashOffset: 0.0,
            pointBackgroundColor: "#1f8ef1",
            pointBorderColor: "rgba(255,255,255,0)",
            pointHoverBackgroundColor: "#1f8ef1",
            pointBorderWidth: 20,
            pointHoverRadius: 4,
            pointHoverBorderWidth: 15,
            pointRadius: 4,
            data: reportData.datasets,
          }
        ]
      };
    },
  }
























  useEffect(() => {
    const request = {
      viewID: props.viewID,
      startDate,
      endDate,
      metrics: "totalUsers",
      dimensions: ["source", "date"],
    };
    setTimeout(
      () =>
        fetchData(request)
          .then((resp) => displayResults(resp))
          .catch((error) => console.error(error)),
      1100
    );
  }, [startDate, endDate, props.viewID, fetchData, displayResults]);

  return (
    <Card className="card-chart shadow-lg border rounded">
      <CardHeader>
        <p className="card-category font-weight-bolder">Top 5 Sources of Visits</p>
        <div className="d-flex justify-content-between">
          <div >
            <CustomDatePicker
              placeholder={"Start date"}
              date={startDate}
              handleDateChange={(date) => setStartDate(date)}
            />
          </div>
          <CustomDatePicker
            placeholder={"End date"}
            date={endDate}
            handleDateChange={(date) => setEndDate(date)}
          />
        </div>
        {`Total sources - ${totalSources}`}
        <p></p>

        <CardTitle tag="p">
        </CardTitle>
      </CardHeader>
      <CardBody className="py-3">

        {reportData && (

          <Bar data={data} options={options} height={300} />

        )}
      </CardBody>
    </Card>

  );
};

export default SourceReport;
